import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddShoppingCartOutlinedIcon from "@mui/icons-material/AddShoppingCartOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import ProductsService from "../../services/productsService";
import { useCart } from "../../context/CartContext";

const toCamelCase = (str) => {
  if (!str) return "<No Brand>";
  const result = str
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ")
    .trim();
  return result || "<No Brand>";
};

export const ShopItem = (props) => {
  const service = ProductsService();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [itemImage, setItemImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);

  const goToDetail = () => {
    navigate(`/item/${encodeURIComponent(props.itemId)}`);
  };

  const stockLabel =
    props.itemAvailability > 20 ? ">20" : `${props.itemAvailability ?? 0}`;
  const outOfStock = (props.itemAvailability ?? 0) <= 0;
  const isValid = quantity >= 1 && quantity <= (props.itemAvailability ?? 0);

  useEffect(() => {
    let cancelled = false;
    if (props.itemImage) {
      setItemImage(props.itemImage);
      setImageLoaded(true);
      return;
    }
    service
      .getProductImages(props.itemId)
      .then((img) => {
        if (!cancelled) {
          setItemImage(img);
          setImageLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setImageLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [props.itemId, props.itemImage]);

  const handleQuantityChange = (e) => {
    const v = e.target.value;
    if (v === "") {
      setQuantity("");
      return;
    }
    const n = parseInt(v, 10);
    if (!Number.isNaN(n)) setQuantity(n);
  };

  const handleAdd = async () => {
    if (!isValid || adding) return;
    setAdding(true);
    try {
      await addItem(props.itemId, quantity);
      if (props.updateCartItemCount) props.updateCartItemCount();
    } finally {
      setAdding(false);
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: (t) => `1px solid ${t.palette.divider}`,
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      {/* Clickable region: image + brand + title navigates to detail */}
      <CardActionArea
        onClick={goToDetail}
        sx={{
          display: "block",
          "&:hover .product-img": { transform: "scale(1.03)" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: 200,
            bgcolor: "background.default",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            p: 2,
          }}
        >
          {!imageLoaded ? (
            <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
          ) : itemImage ? (
            <CardMedia
              component="img"
              className="product-img"
              image={`data:image/jpeg;base64,${itemImage}`}
              alt={props.itemDescription || props.itemName}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                transition: "transform 0.25s ease",
              }}
            />
          ) : (
            <ImageNotSupportedOutlinedIcon
              sx={{ fontSize: 56, color: "text.disabled" }}
            />
          )}
        </Box>

        <CardContent sx={{ pt: 2, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              fontWeight: 700,
              fontSize: 11,
            }}
          >
            {toCamelCase(props.itemBrand)}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: 44,
              mt: 0.5,
            }}
            title={toCamelCase(props.itemDescription)}
          >
            {toCamelCase(props.itemDescription)}
          </Typography>
        </CardContent>
      </CardActionArea>

      {/* Non-clickable footer: price / stock / quantity / add-to-basket */}
      <Box
        sx={{
          px: 2,
          pb: 2,
          pt: 0,
          mt: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            pt: 1.5,
            borderTop: (t) => `1px solid ${t.palette.divider}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: "text.primary" }}>
            € {props.itemPrice ?? 0}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Inventory2OutlinedIcon
              sx={{ fontSize: 16, color: outOfStock ? "error.main" : "text.secondary" }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: outOfStock ? "error.main" : "text.secondary",
              }}
            >
              {stockLabel}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            type="number"
            size="small"
            value={quantity}
            onChange={handleQuantityChange}
            inputProps={{
              min: 1,
              max: props.itemAvailability,
              "aria-label": "Quantity",
              style: { textAlign: "center", fontWeight: 600 },
            }}
            sx={{ width: 76 }}
            disabled={outOfStock}
          />
          <Tooltip title={outOfStock ? "Out of stock" : "Add to basket"}>
            <span>
              <IconButton
                onClick={handleAdd}
                disabled={!isValid || adding || outOfStock}
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  color: "text.primary",
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    borderColor: "primary.main",
                  },
                  "&.Mui-disabled": {
                    opacity: 0.5,
                  },
                }}
                aria-label="Add to basket"
              >
                <AddShoppingCartOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};
